import { Modal, SideModal, createEventData, pushToDataLayer, useToast } from '@arnold/common';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GraphQLErrorCard } from '../../components';
import {
  CreateTeamMutation,
  OrganizationQuery,
  OrganizationTeamsQuery,
  UpdateTeamMutation,
  UpdateTeamMutationFn,
  UserQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
} from '../../generated/hooks';
import { getOrganizationTeams } from '../../graphql/queries';
import TeamForm, { TeamFormStatus } from './TeamForm';
import { TeamsWithSubTeams, TeamWithSubTeams } from './TeamsAndPeople';

interface IProps {
  user: UserQuery['user'];
  organization: NonNullable<OrganizationQuery['organization']>;
  // when team is null, it means we are creating a new one
  team: TeamWithSubTeams | null;
  defaultSupervisorTeamId?: string;
  onClose: (teamId?: string) => void;
  teams: TeamsWithSubTeams;
  teamNames: string[];
  respondents: NonNullable<OrganizationTeamsQuery['organizationTeams']>[0]['members'];
  teamLeaderIds: string[];
  addRespondent: () => void;
  latestCreatedEmployeeId?: string;
  setLatestCreatedEmployeeId: (id: string | undefined) => void;
  wide?: boolean;
  disableCreatingNewPerson?: boolean;
}

export const TeamDetail = ({
  organization,
  team,
  defaultSupervisorTeamId,
  user,
  onClose,
  teams,
  teamNames,
  respondents,
  teamLeaderIds,
  addRespondent,
  latestCreatedEmployeeId,
  setLatestCreatedEmployeeId,
  wide,
  disableCreatingNewPerson,
}: IProps) => {
  const { t } = useTranslation(['peopleChart', 'organizationStructure']);
  const { addToast } = useToast();
  const [opened, setOpened] = useState(true);
  const [formStatus, setFormStatus] = useState<TeamFormStatus>();
  const [saveChangesModalShow, setSaveChangesModalShow] = useState(false);
  const [teamId, setTeamId] = useState<string | undefined>(team?.id);

  const onCloseClicked = () => {
    if (formStatus?.somethingChanged) {
      setSaveChangesModalShow(true);
    } else {
      setOpened(false);
    }
  };

  const onTeamDeleted = () => {
    setOpened(false);
  };

  useEffect(() => {
    setOpened(true);
  }, [team?.id]);

  const [updateTeamMutation, { loading: updateMutationloading, error: updateMutationError }] = useUpdateTeamMutation({
    onCompleted: (data: UpdateTeamMutation) => {
      addToast(
        t('organizationStructure:teamUpdated', {
          name: `${data.updateTeam.name}`,
        }),
      );
      setTeamId(data.updateTeam.id);
      setOpened(false);
    },
    onError: () => {
      pushToDataLayer({
        userId: user!.id,
        orgId: organization.id,
        event: 'ux.team-update-error',
        ...createEventData('organization', 'updateTeamError', 'organization update team error'),
      });
    },
    refetchQueries: [getOrganizationTeams],
  });

  const [createTeamMutation, { loading: createMutationLoading, error: createMutationError }] = useCreateTeamMutation({
    onCompleted: (data: CreateTeamMutation) => {
      addToast(
        t('organizationStructure:createSuccess', {
          name: `${data.createTeam.name}`,
        }),
      );
      setTeamId(data.createTeam.id);
      setOpened(false);
    },
    onError: () => {
      pushToDataLayer({
        userId: user?.id,
        orgId: organization?.id,
        event: 'ux.team-create-error',
        ...createEventData('organization', 'createTeamError', 'organization create team error'),
      });
    },
    refetchQueries: [getOrganizationTeams, 'ReportRecipients'],
    awaitRefetchQueries: true,
  });

  const error = updateMutationError || createMutationError;

  const loading = updateMutationloading || createMutationLoading;

  return (
    <>
      <SideModal
        opened={opened}
        onCloseAction={onCloseClicked}
        onClosedAnimationComplete={() => onClose(teamId)}
        primaryButton={{ label: t('modal:save'), onClick: formStatus?.save }}
        secondaryButton={{ label: t('modal:cancel'), onClick: onCloseClicked }}
        loadingOverlay={loading}
        wide={wide}
        adminZIndex={true}
      >
        <>
          {error ? (
            <GraphQLErrorCard />
          ) : (
            <TeamForm
              organization={organization}
              team={team}
              teams={teams}
              teamNames={teamNames}
              defaultSupervisorTeamId={defaultSupervisorTeamId}
              respondents={respondents}
              teamLeaderIds={teamLeaderIds}
              setFormStatus={setFormStatus}
              formStatus={formStatus}
              saveTeam={
                !formStatus?.somethingChanged
                  ? (onCloseClicked as UpdateTeamMutationFn)
                  : team
                    ? updateTeamMutation
                    : createTeamMutation
              }
              onDeletionComplete={onTeamDeleted}
              addRespondent={addRespondent}
              latestCreatedEmployeeId={latestCreatedEmployeeId}
              setLatestCreatedEmployeeId={setLatestCreatedEmployeeId}
              disableCreatingNewPerson={disableCreatingNewPerson}
            />
          )}
        </>
      </SideModal>
      <Modal
        show={saveChangesModalShow}
        onSubmit={formStatus?.save}
        onCancel={() => setOpened(false)}
        onHide={() => setSaveChangesModalShow(false)}
        title={t('organizationStructure:saveChangesModalTitle')}
        text={t('organizationStructure:saveChangesModalText')}
        buttons={{
          submit: { title: t('organizationStructure:saveChangesModalSubmit') },
          cancel: { title: t('organizationStructure:saveChangesModalCancel') },
        }}
      />
    </>
  );
};
